import { axiosInstance } from "./axiosInstance";

export interface SupportChatbotMessage {
  id: string;
  content: string;
  sender: "user" | "bot";
  timestamp: Date;
  needsEscalation?: boolean;
  escalationEmailSent?: boolean;
}

export interface SupportChatbotConversation {
  id: string;
  messages: SupportChatbotMessage[];
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

export interface SendMessageData {
  message: string;
  language?: string;
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
  needsEscalation?: boolean;
  escalationEmailSent?: boolean;
}

class SupportChatbotService {
  /**
   * Enviar mensaje al chatbot público (sin autenticación)
   */
  async sendPublicMessage(data: SendMessageData): Promise<SendMessageResponse> {
    try {
      const response = await axiosInstance.post(
        "/api/support-chatbot/public/message",
        {
          message: data.message,
          language: data.language || "es",
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error sending public message:", error);
      throw new Error(error.response?.data?.error || "Error al enviar mensaje");
    }
  }

  /**
   * Enviar mensaje al chatbot autenticado
   */
  async sendAuthenticatedMessage(
    data: SendMessageData
  ): Promise<SendMessageResponse> {
    try {
      const response = await axiosInstance.post(
        "/api/support-chatbot/message",
        {
          message: data.message,
          language: data.language || "es",
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error sending authenticated message:", error);
      throw new Error(error.response?.data?.error || "Error al enviar mensaje");
    }
  }

  /**
   * Enviar ticket de soporte humano
   */
  async sendSupportTicket(data: {
    email: string;
    description: string;
  }): Promise<void> {
    try {
      await axiosInstance.post("/api/support-chatbot/ticket", data);
    } catch (error: any) {
      console.error("Error sending support ticket:", error);
      throw new Error(
        error.response?.data?.error || "Error al enviar ticket de soporte"
      );
    }
  }

  /**
   * Obtener historial de conversaciones
   */
  async getConversationHistory(
    page: number = 1,
    limit: number = 20
  ): Promise<{
    conversations: SupportChatbotConversation[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await axiosInstance.get("/api/support-chatbot/history", {
        params: { page, limit },
      });

      return response.data;
    } catch (error: any) {
      console.error("Error getting conversation history:", error);
      throw new Error(
        error.response?.data?.error || "Error al obtener historial"
      );
    }
  }

  /**
   * Guardar conversación
   */
  async saveConversation(data: {
    message: string;
    response: string;
    needsEscalation?: boolean;
  }): Promise<void> {
    try {
      await axiosInstance.post("/api/support-chatbot/save", data);
    } catch (error: any) {
      console.error("Error saving conversation:", error);
      throw new Error(
        error.response?.data?.error || "Error al guardar conversación"
      );
    }
  }

  /**
   * Obtener estadísticas del chatbot (solo para admins)
   */
  async getChatbotStats(): Promise<{
    totalConversations: number;
    totalMessages: number;
    escalations: number;
    averageResponseTime: number;
  }> {
    try {
      const response = await axiosInstance.get("/api/support-chatbot/stats");
      return response.data.stats;
    } catch (error: any) {
      console.error("Error getting chatbot stats:", error);
      throw new Error(
        error.response?.data?.error || "Error al obtener estadísticas"
      );
    }
  }

  /**
   * Generar ID único para mensajes
   */
  generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Crear mensaje de usuario
   */
  createUserMessage(content: string): SupportChatbotMessage {
    return {
      id: this.generateMessageId(),
      content,
      sender: "user",
      timestamp: new Date(),
    };
  }

  /**
   * Crear mensaje del bot
   */
  createBotMessage(
    content: string,
    needsEscalation?: boolean,
    escalationEmailSent?: boolean
  ): SupportChatbotMessage {
    return {
      id: this.generateMessageId(),
      content,
      sender: "bot",
      timestamp: new Date(),
      needsEscalation,
      escalationEmailSent,
    };
  }
}

export default new SupportChatbotService();
